// src/pages/PostCard/index.jsx
import { useState, useEffect } from 'react';
import { Row, Col, Input, Card, Avatar, Tag, Typography, Spin, Empty, Button, message, Pagination } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { SearchOutlined, EyeOutlined, LikeOutlined, MessageOutlined, EditOutlined } from '@ant-design/icons';
import { fakeApi } from '../../api/fakeApi';

const { Search } = Input;
const { Text } = Typography;

const PostCard = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9); // 默认每页显示9篇文章
  const [totalPosts, setTotalPosts] = useState(0);

  // 从API加载文章
  useEffect(() => {
    fetchPosts();
  }, [currentPage, pageSize, searchText]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await fakeApi.posts.getPosts(currentPage, pageSize, searchText);
      if (response.success) {
        setPosts(response.data.posts);
        setTotalPosts(response.data.pagination.total);
      } else {
        message.error('加载文章失败');
      }
    } catch (error) {
      console.error('获取文章失败:', error);
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1); // 搜索时重置到第一页
  };

  // 分页变化处理
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // 每页数量变化时回到第一页
    }
  };

  // 获取安全的作者头像
  const getSafeAvatar = (post) => {
    if (post.authorAvatar && typeof post.authorAvatar === 'string') {
      return post.authorAvatar;
    }
    if (post.author && typeof post.author === 'string') {
      return post.author.charAt(0);
    }
    return '匿';
  };

  // 获取安全的作者名
  const getSafeAuthor = (post) => {
    if (post.author && typeof post.author === 'string') {
      return post.author;
    }
    return '匿名用户';
  };

  // 获取安全的日期
  const getSafeDate = (post) => {
    if (post.date && typeof post.date === 'string') {
      return post.date;
    }
    return '未知日期';
  };

  // 获取安全的标题
  const getSafeTitle = (post) => {
    if (post.title && typeof post.title === 'string') {
      return post.title;
    }
    return '无标题';
  };

  // 获取安全的标签数组
  const getSafeTags = (post) => {
    if (Array.isArray(post.tags)) {
      return post.tags;
    }
    return [];
  };

  if (loading && posts.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', marginTop: 40 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>加载文章中...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* 搜索框 - 修改为相对定位 */}
      <div style={{ 
        maxWidth: 500, 
        margin: '0 auto 24px',
        position: 'relative',  // 改为相对定位
        zIndex: 1,
        padding: '0'
      }}>
        <Search
          placeholder="搜索文章..."
          enterButton={<SearchOutlined />}
          size="large"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          loading={loading}
          style={{ width: '100%' }}
        />
      </div>

      {/* 搜索结果统计 */}
      {searchText && (
        <div style={{ marginBottom: 16, textAlign: 'center' }}>
          <Text type="secondary">
            搜索 "<Text strong>{searchText}</Text>" 找到 {totalPosts} 篇文章
          </Text>
        </div>
      )}

      {/* 文章列表 */}
      {posts.length === 0 ? (
        <Card>
          <Empty 
            description={
              <div>
                <div style={{ marginBottom: 16 }}>暂无文章</div>
                <Button 
                  type="primary" 
                  onClick={() => {
                    const user = localStorage.getItem('user');
                    if (!user) {
                      message.warning('请先登录再写文章');
                      navigate('/auth');
                    } else {
                      navigate('/write');
                    }
                  }}
                >
                  去写第一篇
                </Button>
              </div>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {posts.map(post => {
              const safeAvatar = getSafeAvatar(post);
              const safeAuthor = getSafeAuthor(post);
              const safeDate = getSafeDate(post);
              const safeTitle = getSafeTitle(post);
              const safeTags = getSafeTags(post);
              
              // 检查当前用户是否是文章作者
              const currentUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
              const isAuthor = currentUser && currentUser.name === safeAuthor;
              
              return (
                <Col xs={24} sm={12} md={8} key={post.id}>
                  <div style={{ position: 'relative' }}>
                    <Link to={`/post/${post.id}`}>
                      <Card hoverable size="small" style={{ height: '100%' }}>
                        {/* 文章标题 */}
                        <div style={{ marginBottom: 12, minHeight: '44px' }}>
                          <Text strong style={{ fontSize: 16, lineHeight: 1.4 }}>
                            {safeTitle}
                          </Text>
                        </div>

                        {/* 作者信息 */}
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                          <Avatar 
                            size="small" 
                            style={{ 
                              backgroundColor: '#1890ff', 
                              marginRight: 8,
                              fontSize: 12,
                              flexShrink: 0
                            }}
                          >
                            {safeAvatar}
                          </Avatar>
                          <div style={{ flex: 1 }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                              {safeAuthor}
                            </Text>
                            <div style={{ fontSize: 11, color: '#999' }}>
                              {safeDate}
                            </div>
                          </div>
                        </div>

                        {/* 文章标签 */}
                        <div style={{ marginBottom: 12, minHeight: '28px' }}>
                          {safeTags.slice(0, 3).map(tag => (
                            <Tag 
                              key={tag} 
                              style={{ 
                                fontSize: 10, 
                                margin: '2px 4px 2px 0',
                                padding: '0 6px'
                              }}
                            >
                              {tag}
                            </Tag>
                          ))}
                          {safeTags.length > 3 && (
                            <span style={{ fontSize: 10, color: '#999' }}>
                              +{safeTags.length - 3}
                            </span>
                          )}
                        </div>

                        {/* 统计数据 */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          fontSize: 12,
                          color: '#999',
                          marginTop: 'auto'
                        }}>
                          <span>
                            <EyeOutlined style={{ marginRight: 4 }} />
                            {typeof post.views === 'number' ? post.views : 0}
                          </span>
                          <span>
                            <LikeOutlined style={{ marginRight: 4 }} />
                            {typeof post.likes === 'number' ? post.likes : 0}
                          </span>
                          <span>
                            <MessageOutlined style={{ marginRight: 4 }} />
                            {typeof post.comments === 'number' ? post.comments : 0}
                          </span>
                        </div>
                      </Card>
                    </Link>
                    
                    {/* 编辑按钮（仅作者可见） */}
                    {isAuthor && (
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/write/${post.id}`);
                        }}
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          color: '#666',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}
                        title="编辑文章"
                      />
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>

          {/* 分页器 */}
          <div style={{ 
            marginTop: 32, 
            display: 'flex', 
            justifyContent: 'center'
          }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalPosts}
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`}
              pageSizeOptions={['6', '9', '12', '18']}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PostCard;